import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Check, ChevronsUpDown } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { statusOptions, today } from "@/components/invoiceTrackingUtils";
import { TypedDateInput } from "@/components/TypedDateInput";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/operations`;
const COMPANY_WIDE_OPTION = "__company_wide__";

const projectCode = (project) =>
  project.project_number || project.project_code || project.id;

const projectName = (project) =>
  project.project_name || project.name || projectCode(project);

const projectLabel = (project) => `${projectCode(project)} — ${projectName(project)}`;

export function InvoiceEditorDialog({
  editor,
  lockedProjectId = "",
  lockedProjectLabel = "",
  onOpenChange,
  onSaved,
  projects,
}) {
  const projectButtonLabel = () => {
    if (form?.unlinked_whole_company) {
      return "Not linked to a project (Company-wide)";
    }
    if (selectedProject) {
      return projectLabel(selectedProject);
    }
    return "Select a Project...";
  };
  const saveButtonLabel = () => {
    if (saving) {
      return "Saving…";
    }
    if (editor?.mode === "create") {
      return "Save tracking record";
    }
    return "Save invoice update";
  };
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [projectPickerOpen, setProjectPickerOpen] = useState(false);
  const [projectQuery, setProjectQuery] = useState("");
  const activeProjects = useMemo(
    () =>
      projects.filter(
        (project) => !["closed", "canceled"].includes(project.lifecycle_state),
      ),
    [projects],
  );
  const matchingProjects = useMemo(() => {
    const query = projectQuery.trim().toLocaleLowerCase();
    const rank = (project) => {
      const code = projectCode(project).toLocaleLowerCase();
      const name = projectName(project).toLocaleLowerCase();
      if (!query || code.startsWith(query)) return 0;
      if (code.includes(query)) return 1;
      if (name.startsWith(query)) return 2;
      return 3;
    };
    return activeProjects
      .filter((project) => {
        const code = projectCode(project).toLocaleLowerCase();
        const name = projectName(project).toLocaleLowerCase();
        return !query || code.includes(query) || name.includes(query);
      })
      .sort((first, second) => {
        const rankDifference = rank(first) - rank(second);
        return rankDifference || projectCode(first).localeCompare(projectCode(second));
      });
  }, [activeProjects, projectQuery]);
  const selectedProject = activeProjects.find(
    (project) => project.id === form?.project_id,
  );
  const lockedProject = projects.find((project) => project.id === lockedProjectId);
  useEffect(() => {
    if (editor && editor.mode !== "payment") {
      setError("");
      setProjectPickerOpen(false);
      setProjectQuery("");
      const source = editor.invoice || {};
      setForm({
        project_id: lockedProjectId || source.project_id || "",
        invoice_number: source.invoice_number || "",
        project_number: lockedProjectId
          ? projectCode(lockedProject || {})
          : source.project_number || "",
        customer: lockedProjectId
          ? lockedProject?.client || source.customer || ""
          : source.customer || "",
        invoice_date: source.invoice_date || today(),
        invoice_amount: String(source.invoice_amount || ""),
        work_description: source.work_description || source.notes || "",
        status: source.status || "waiting_to_submit",
        owner: source.owner || "Office/Admin",
        date_submitted: source.date_submitted || "",
        date_approved: source.date_approved || "",
        paid_amount: source.paid_amount || 0,
        payment_date: source.payment_date || "",
        related_article_ids: source.related_article_ids || [],
        notes: source.notes || "",
        unlinked_whole_company: lockedProjectId ? false : Boolean(source.unlinked_whole_company),
      });
    } else {
      setForm(null);
    }
  }, [editor, lockedProject, lockedProjectId]);
  const setValue = (key, value) =>
    setForm((current) => ({ ...current, [key]: value }));
  const setProject = (projectId) => {
    if (projectId === COMPANY_WIDE_OPTION) {
      setForm((current) => ({
        ...current,
        project_id: "",
        project_number: "",
        customer: "",
        unlinked_whole_company: true,
      }));
      setProjectPickerOpen(false);
      setProjectQuery("");
      return;
    }

    if (!projectId) {
      setForm((current) => ({
        ...current,
        project_id: "",
        project_number: "",
        customer: "",
        unlinked_whole_company: false,
      }));
      setProjectPickerOpen(false);
      setProjectQuery("");
      return;
    }

    const project = projects.find((item) => item.id === projectId);
    setForm((current) => ({
      ...current,
      project_id: projectId,
      project_number: project?.project_number || "",
      customer: project?.customer || "",
      unlinked_whole_company: false,
    }));
    setProjectPickerOpen(false);
    setProjectQuery("");
  };
  const save = async (event) => {
    event.preventDefault();
    setError("");
    if (!form.project_id && !form.unlinked_whole_company) {
      setError("Select a Project or choose Not linked to a project (Company-wide).");
      return;
    }
    if (!form || !editor || editor.mode === "payment") return;
    const editorMode = editor.mode;
    const invoiceId = editor.invoice?.id;
    setSaving(true);
    const payload = {
      ...form,
      project_id: lockedProjectId || form.project_id,
      project_number: lockedProjectId
        ? projectCode(lockedProject || {})
        : form.project_number,
      customer: lockedProjectId
        ? lockedProject?.client || form.customer
        : form.customer,
      unlinked_whole_company: lockedProjectId ? false : form.unlinked_whole_company,
      invoice_amount: Number(form.invoice_amount || 0),
      paid_amount: Number(form.paid_amount || 0),
    };
    try {
      if (editorMode === "create")
        await axios.post(
          lockedProjectId
            ? `${API}/projects/${lockedProjectId}/invoices`
            : `${API}/invoices`,
          payload,
        );
      else if (invoiceId) {
        await axios.patch(
          lockedProjectId
            ? `${API}/projects/${lockedProjectId}/invoices/${invoiceId}`
            : `${API}/invoices/${invoiceId}`,
          payload,
        );
      }
      toast.success(
        editorMode === "create"
          ? "Invoice tracking record created."
          : "Invoice tracking record updated.",
      );
      onSaved();
    } catch (error) {
      const detail = error.response?.data?.detail;
      let message = "Invoice could not be saved.";
      if (typeof detail === "string") {
        message = detail;
      } else if (detail && typeof detail === "object") {
        if (detail.error === "invoice_number_already_exists") {
          const loc = (detail.collision_location || "another invoice").toLowerCase();
          const proj = detail.existing_project_code ? ` on project ${detail.existing_project_code}` : "";
          message = `Invoice "${detail.existing_invoice_number}" already exists — ${loc}${proj}.`;
        } else if (detail.message) {
          message = detail.message;
        }
      }
      setError(message);
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };
  if (!editor || editor.mode === "payment" || !form) return null;
  return (
    <Dialog onOpenChange={onOpenChange} open={Boolean(editor)}>
      <DialogContent
        className={
          `invoice-editor-dialog ${lockedProjectId ? "project-invoice-drawer" : ""}`
        }
        data-testid={
          lockedProjectId ? "project-invoice-drawer" : "invoice-editor-dialog"
        }
      >
        <DialogHeader>
          <p className="operations-kicker">Office billing workflow</p>
          <DialogTitle>
            {editor?.mode === "create"
              ? "Track existing invoice"
              : `Update ${editor?.invoice?.invoice_number}`}
          </DialogTitle>
          <DialogDescription>
            Use this workspace to track creation, sending, approval, changes,
            and payment—not to generate invoice documents.
          </DialogDescription>
        </DialogHeader>
        {error && <p data-testid="invoice-editor-error">{error}</p>}
        {form && (
          <form
            className="invoice-editor-form"
            data-testid="invoice-editor-form"
            onSubmit={save}
          >
            {lockedProjectId ? (
              <div data-testid="project-invoice-fixed-project">
                <span>Project</span>
                <strong>{lockedProjectLabel}</strong>
              </div>
            ) : (
              <div className="invoice-project-picker">
                <span id="invoice-project-label">Project</span>
                <Popover
                  onOpenChange={setProjectPickerOpen}
                  open={projectPickerOpen}
                >
                  <PopoverTrigger asChild>
                    <button
                      aria-expanded={projectPickerOpen}
                      aria-labelledby="invoice-project-label"
                      className="invoice-project-picker-trigger"
                      data-testid="invoice-project-select"
                      type="button"
                    >
                      <span>
                        {projectButtonLabel()}
                      </span>
                      <ChevronsUpDown aria-hidden="true" size={15} />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent
                    align="start"
                    className="invoice-project-popover"
                    sideOffset={6}
                  >
                    <Command shouldFilter={false}>
                      <CommandInput
                        data-testid="invoice-project-search-input"
                        onValueChange={setProjectQuery}
                        placeholder="Search project code or name..."
                        value={projectQuery}
                      />
                      <CommandList>
                        <CommandGroup heading="All active projects">
                          {matchingProjects.map((project) => (
                            <CommandItem
                              data-testid={`invoice-project-option-${project.id}`}
                              key={project.id}
                              onSelect={() => setProject(project.id)}
                              value={projectLabel(project)}
                            >
                              <Check
                                aria-hidden="true"
                                className={
                                  form.project_id === project.id
                                    ? "invoice-project-check-visible"
                                    : "invoice-project-check-hidden"
                                }
                                size={14}
                              />
                              {projectLabel(project)}
                            </CommandItem>
                          ))}
                          {!matchingProjects.length && (
                            <p
                              className="invoice-project-no-results"
                              data-testid="invoice-project-no-results"
                            >
                              No active projects match that search.
                            </p>
                          )}
                        </CommandGroup>
                        <CommandGroup heading="Project relationship">
                          <CommandItem
                            data-testid="invoice-company-wide-option"
                            onSelect={() => setProject(COMPANY_WIDE_OPTION)}
                            value="Not linked to a project Company-wide"
                          >
                            <Check
                              aria-hidden="true"
                              className={
                                form.unlinked_whole_company
                                  ? "invoice-project-check-visible"
                                  : "invoice-project-check-hidden"
                              }
                              size={14}
                            />
                            Not linked to a project (Company-wide)
                          </CommandItem>
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
            )}
            {lockedProjectId ? (
              <div data-testid="project-invoice-fixed-project-number">
                <span>Project number</span>
                <strong>{form.project_number || lockedProjectLabel}</strong>
              </div>
            ) : (
              <label htmlFor="invoice-project-number">
                Job Number
                <Input
                  data-testid="invoice-project-number-input"
                  id="invoice-project-number"
                  onChange={(event) => {
                    setValue("project_number", event.target.value);
                  }}
                  value={form.project_number}
                />
              </label>
            )}
            <label htmlFor="invoice-number">
              Invoice Number
              <Input
                data-testid="invoice-number-input"
                id="invoice-number"
                onChange={(event) =>
                  setValue("invoice_number", event.target.value)
                }
                required
                value={form.invoice_number}
              />
            </label>
            <label htmlFor="invoice-date">
              Date Sent
              <TypedDateInput
                data-testid="invoice-date-input"
                id="invoice-date"
                onChange={(value) => setValue("date_submitted", value)}
                value={form.date_submitted}
              />
            </label>
            <label htmlFor="invoice-status">
              Approval State
              <select
                data-testid="invoice-status-select"
                id="invoice-status"
                onChange={(event) => setValue("status", event.target.value)}
                value={form.status}
              >
                {statusOptions.map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
            <label htmlFor="invoice-amount">
              Invoice Value
              <Input
                data-testid="invoice-amount-input"
                id="invoice-amount"
                min="0"
                onChange={(event) =>
                  setValue("invoice_amount", event.target.value)
                }
                required
                step="0.01"
                type="number"
                value={form.invoice_amount}
              />
            </label>
            <label htmlFor="invoice-owner">
              Office Owner
              <Input
                data-testid="invoice-owner-input"
                id="invoice-owner"
                onChange={(event) => setValue("owner", event.target.value)}
                value={form.owner}
              />
            </label>
            <label
              className="invoice-editor-wide"
              htmlFor="invoice-work-description"
            >
              Value / Work Invoice Is For
              <Textarea
                data-testid="invoice-work-description-input"
                id="invoice-work-description"
                onChange={(event) =>
                  setValue("work_description", event.target.value)
                }
                value={form.work_description}
              />
            </label>
            <label className="invoice-editor-wide" htmlFor="invoice-notes">
              Admin Notes / Requested Changes
              <Textarea
                data-testid="invoice-notes-input"
                id="invoice-notes"
                onChange={(event) => setValue("notes", event.target.value)}
                value={form.notes}
              />
            </label>
            <DialogFooter className="invoice-editor-wide">
              <Button
                data-testid="invoice-save-button"
                disabled={saving}
                type="submit"
              >
                {saveButtonLabel()}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

